import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL, getAuthHeaders } from '../services/api';
import './MiddleOfficePages.css';

const currencyOptions = ['LKR', 'USD', 'EUR', 'GBP', 'JPY', 'INR'];

// Highest middle-office tier this user holds, mirrors the backend's
// getMiddleOfficeTier (utils/workflowStageAuth.js) - a middle_office_user's
// submissions are staged for approval; officer/manager/admin act directly
// and may approve/reject others' proposals.
function getMiddleOfficeTier() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.role === 'admin') return 'admin';
    const rank = { middle_office_user: 1, middle_office_officer: 2, middle_office_manager: 3 };
    let best = null;
    for (const a of user.assignments || []) {
      if (rank[a.role] && (!best || rank[a.role] > rank[best])) best = a.role;
    }
    return best;
  } catch {
    return null;
  }
}

export default function DealerLimitSetupPage() {
  const [users, setUsers] = useState([]);
  const [limits, setLimits] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [perDealLimit, setPerDealLimit] = useState('');
  const [perDayLimit, setPerDayLimit] = useState('');
  const [currency, setCurrency] = useState('LKR');
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const tier = useMemo(getMiddleOfficeTier, []);
  const isApprover = tier === 'admin' || tier === 'middle_office_officer' || tier === 'middle_office_manager';
  const isProposer = tier === 'middle_office_user';

  const fetchLimits = () => {
    fetch(`${API_BASE_URL}/dealer-limits`, { headers: getAuthHeaders() })
      .then((r) => r.json())
      .then((data) => setLimits(data.success ? (data.data || []) : []))
      .catch(() => setLimits([]));
  };

  useEffect(() => {
    fetch(`${API_BASE_URL}/users`, { headers: getAuthHeaders() })
      .then((r) => r.json())
      .then((data) => setUsers(Array.isArray(data) ? data : (data.users || [])));
    fetchLimits();
  }, []);

  useEffect(() => {
    if (!successMsg && !errorMsg) return undefined;
    const t = setTimeout(() => {
      setSuccessMsg('');
      setErrorMsg('');
    }, 4000);
    return () => clearTimeout(t);
  }, [successMsg, errorMsg]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedUser || !perDealLimit || !perDayLimit) {
      setErrorMsg('Select a user and enter both limits.');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/dealer-limits`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          user_id: selectedUser,
          per_deal_limit: perDealLimit,
          per_day_limit: perDayLimit,
          currency
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccessMsg(data.pendingApproval
          ? 'Submitted for approval — Middle Office Officer/Manager will review it.'
          : 'Dealer limit saved.');
        setErrorMsg('');
        fetchLimits();
      } else {
        setErrorMsg(data.error || 'Failed to save dealer limit.');
      }
    } catch (err) {
      setErrorMsg('Failed to save dealer limit.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this dealer limit?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/dealer-limits/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (res.ok) fetchLimits();
    } catch (err) {
      setErrorMsg('Failed to delete dealer limit.');
    }
  };

  const handleApprove = async (id) => {
    try {
      const res = await fetch(`${API_BASE_URL}/dealer-limits/${id}/approve`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccessMsg('Proposal approved.');
        fetchLimits();
      } else {
        setErrorMsg(data.error || 'Failed to approve proposal.');
      }
    } catch (err) {
      setErrorMsg('Failed to approve proposal.');
    }
  };

  const handleReject = async (id) => {
    try {
      const res = await fetch(`${API_BASE_URL}/dealer-limits/${id}/reject`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason: rejectReason })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccessMsg('Proposal rejected.');
        setRejectingId(null);
        setRejectReason('');
        fetchLimits();
      } else {
        setErrorMsg(data.error || 'Failed to reject proposal.');
      }
    } catch (err) {
      setErrorMsg('Failed to reject proposal.');
    }
  };

  return (
    <div className="middle-office-page ui-page">
      <p className="page-intro">
        Per-user dealing limits for front-office deal entry. If a saved deal exceeds a user's per-deal
        or per-day limit, the deal still saves — the entering user and Middle Office are notified
        (in-app and by email) instead of the save being blocked.
        {isProposer && ' As a Middle Office User, changes you submit here require approval from an Officer or Manager before they take effect.'}
      </p>

      {successMsg && <div className="success-message">{successMsg}</div>}
      {errorMsg && <div className="error-message">{errorMsg}</div>}

      <form className="master-form" onSubmit={handleSave}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="dl-user">User <span className="required">*</span></label>
            <select
              id="dl-user"
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              required
            >
              <option value="">Select User</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.username}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="dl-per-deal">Per Deal Limit <span className="required">*</span></label>
            <input
              id="dl-per-deal"
              type="number"
              min="0"
              step="any"
              value={perDealLimit}
              onChange={(e) => setPerDealLimit(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="dl-per-day">Per Day Limit <span className="required">*</span></label>
            <input
              id="dl-per-day"
              type="number"
              min="0"
              step="any"
              value={perDayLimit}
              onChange={(e) => setPerDayLimit(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="dl-currency">Currency</label>
            <select id="dl-currency" value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {currencyOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="form-actions">
          <button type="submit" className="submit-btn">
            {isProposer ? 'Submit for Approval' : 'Save Dealer Limit'}
          </button>
        </div>
      </form>

      <div className="search-section">
        <h3>Current Dealer Limits</h3>
        <table className="master-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Per Deal Limit</th>
              <th>Per Day Limit</th>
              <th>Currency</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {limits.length === 0 && (
              <tr className="empty-row">
                <td colSpan="6">No dealer limits configured yet.</td>
              </tr>
            )}
            {limits.map((l) => {
              const hasPending = l.pending_per_deal_limit !== null || l.pending_per_day_limit !== null;
              return (
                <React.Fragment key={l.id}>
                  <tr>
                    <td>{l.username || l.user_id}</td>
                    <td>{Number(l.per_deal_limit).toLocaleString()}</td>
                    <td>{Number(l.per_day_limit).toLocaleString()}</td>
                    <td>{l.currency}</td>
                    <td>
                      {hasPending
                        ? <span className="mo-status mo-status--pending">Pending approval</span>
                        : <span className="mo-status mo-status--active">Active</span>}
                    </td>
                    <td>
                      <div className="mo-actions">
                        <button type="button" className="delete-btn" onClick={() => handleDelete(l.id)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                  {hasPending && (
                    <tr className="mo-pending-row">
                      <td colSpan="6">
                        Proposed by {l.proposed_by_username || l.proposed_by}: per-deal{' '}
                        <strong>{Number(l.pending_per_deal_limit || 0).toLocaleString()}</strong>, per-day{' '}
                        <strong>{Number(l.pending_per_day_limit || 0).toLocaleString()}</strong>
                        {isApprover && (
                          <div className="mo-pending-actions">
                            <button type="button" className="approve-btn" onClick={() => handleApprove(l.id)}>
                              Approve
                            </button>
                            {rejectingId === l.id ? (
                              <>
                                <input
                                  type="text"
                                  placeholder="Rejection reason"
                                  value={rejectReason}
                                  onChange={(e) => setRejectReason(e.target.value)}
                                />
                                <button type="button" className="delete-btn" onClick={() => handleReject(l.id)}>
                                  Confirm Reject
                                </button>
                                <button
                                  type="button"
                                  className="cancel-btn"
                                  onClick={() => { setRejectingId(null); setRejectReason(''); }}
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <button type="button" className="delete-btn" onClick={() => setRejectingId(l.id)}>
                                Reject
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
